import React, { useMemo } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { DomainIcon } from '@patternfly/react-icons';
import { formatDateNumeric } from '../../../../utils/dateFormat';
import type { DomainName } from '../../../../types/models';
import { MAX_TRADE_CART, isBeDomain, tradeIneligibilityReason, tradeRejectionCopy } from './types';

interface TradeDomainsStepProps {
  domainNames: DomainName[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** The FQDNs currently picked for the owner change. */
  selected: string[];
  onToggle: (name: string, isSelecting: boolean) => void;
  /** Bulk-select/deselect the given (eligible) FQDNs — the header select-all checkbox. */
  onToggleAll: (names: string[], isSelecting: boolean) => void;
  /** Per-name create-time rejection codes keyed by FQDN — flag the matching rows after a failed submit. */
  itemErrors: Record<string, string>;
}

/** The current registrant summary carried on the list payload, for the "you are changing THIS owner" column. */
function currentOwnerLabel(domain: DomainName): string {
  const contact = domain.registrant_contact;
  if (!contact) {
    return '—';
  }
  const name = `${contact.first_name} ${contact.last_name}`.trim();
  return contact.organization ? `${name} — ${contact.organization}` : name;
}

const TradeDomainsStep: React.FunctionComponent<TradeDomainsStepProps> = ({
  domainNames,
  loading,
  error,
  onRetry,
  selected,
  onToggle,
  onToggleAll,
  itemErrors,
}) => {
  // The feature is `.be`-only, so other extensions are filtered out entirely rather than shown as
  // permanently disabled noise; ineligible `.be` rows stay visible with their reason.
  const beDomains = useMemo(
    () => domainNames.filter((d) => isBeDomain(d.name)).sort((a, b) => a.name.localeCompare(b.name)),
    [domainNames],
  );
  const eligibleNames = useMemo(
    () => beDomains.filter((d) => tradeIneligibilityReason(d) === null).map((d) => d.name),
    [beDomains],
  );

  const allSelected = eligibleNames.length > 0 && eligibleNames.every((name) => selected.includes(name));
  const someSelected = selected.length > 0 && !allSelected;
  const cartFull = selected.length >= MAX_TRADE_CART;

  if (loading) {
    return (
      <Bullseye>
        <Spinner size="xl" aria-label="Loading your domains" />
      </Bullseye>
    );
  }

  if (error) {
    return (
      <Alert
        variant="danger"
        isInline
        title="Unable to load your domains"
        actionLinks={
          <Button variant="link" isInline onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (beDomains.length === 0) {
    return (
      <EmptyState titleText="No .be domains" icon={DomainIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>
          Owner changes are available for <strong>.be</strong> domains registered with us — you don&apos;t have any yet.
          Register or transfer a .be domain first.
        </EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="link" isInline onClick={onRetry}>
              Refresh the list
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  return (
    <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
      <FlexItem>
        <Title headingLevel="h2" size="lg">
          Domains
        </Title>
        <Content component="p">
          Pick the <strong>.be</strong> domain(s) whose owner (registrant) you want to change. Only .be domains
          registrar-managed by us are eligible — other extensions are not shown.
        </Content>
      </FlexItem>

      <FlexItem>
        <Table aria-label="Your .be domains eligible for an owner change" variant="compact">
          <Thead>
            <Tr>
              <Th
                select={{
                  onSelect: (_event, isSelecting) => onToggleAll(eligibleNames, isSelecting),
                  isSelected: allSelected,
                  isIndeterminate: someSelected,
                  isHeaderSelectDisabled: eligibleNames.length === 0,
                }}
                aria-label="Select all eligible domains"
              />
              <Th>Domain</Th>
              <Th>Current owner</Th>
              <Th>Registrar</Th>
              <Th>Expires</Th>
            </Tr>
          </Thead>
          <Tbody>
            {beDomains.map((d, rowIndex) => {
              const reason = tradeIneligibilityReason(d);
              const isSelected = selected.includes(d.name);
              const rowError = itemErrors[d.name];
              return (
                <Tr key={d.name}>
                  <Td
                    select={{
                      rowIndex,
                      isSelected,
                      // Ineligible rows can never be picked; eligible ones lock once the cart is
                      // full (backend cap), except the already-selected ones (still deselectable).
                      isDisabled: reason !== null || (cartFull && !isSelected),
                      onSelect: (_event, isSelecting) => onToggle(d.name, isSelecting),
                    }}
                  />
                  <Td dataLabel="Domain">
                    {d.name}
                    {reason !== null && (
                      <FormHelperText>
                        <HelperText>
                          <HelperTextItem>{reason}</HelperTextItem>
                        </HelperText>
                      </FormHelperText>
                    )}
                    {rowError && (
                      <FormHelperText>
                        <HelperText>
                          <HelperTextItem variant="error">{tradeRejectionCopy(rowError)}</HelperTextItem>
                        </HelperText>
                      </FormHelperText>
                    )}
                  </Td>
                  <Td dataLabel="Current owner">{currentOwnerLabel(d)}</Td>
                  <Td dataLabel="Registrar">{d.domain_registration_platform?.name ?? '—'}</Td>
                  <Td dataLabel="Expires">{formatDateNumeric(d.expires_at)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </FlexItem>

      <FlexItem>
        <HelperText>
          <HelperTextItem>
            {selected.length === 0
              ? 'Select at least one domain to continue.'
              : `${selected.length} domain${selected.length === 1 ? '' : 's'} selected${
                  cartFull ? ` — that's the maximum (${MAX_TRADE_CART}) for a single order` : ''
                }. The new owner contact you enter next applies to every selected domain.`}
          </HelperTextItem>
        </HelperText>
      </FlexItem>
    </Flex>
  );
};

export default TradeDomainsStep;
